/**
 * <chart-south> — South Indian kundali: a 4×4 grid with fixed rāśis and a moving
 * lagna marker.
 *
 * ⚠ **Rāśi-fixed**, the opposite of <chart-north>. Meṣa is always the second cell
 * of the top row and never moves; the lagna is marked wherever it falls. the śāstra
 * states the distinction directly (§1.3.4, Figure 1), and rendering this with
 * North Indian logic gives a chart that looks right and is not.
 *
 * ◆ Because the rāśis are fixed, this style works **without a birth time** — it
 * needs no bhāvas. That is not a detail: it is the only chart style this site can
 * honestly draw for a birth with no recorded hour, and the reason the kundali
 * page switches to it automatically in that case.
 *
 * © Manish Jagdish Thatte
 */

import { SOUTH_CELLS, SOUTH_CENTRE, el, renderCellLabels } from '../utils/svg.js';
import { graha, rashi } from '../jyotish/constants.js';
import { localName } from '../utils/i18n.js';

export class ChartSouth extends HTMLElement {
  static observedAttributes = ['locale', 'labels', 'title-text'];

  connectedCallback() { this.render(); }
  attributeChangedCallback() { if (this.isConnected) this.render(); }

  set kundali(value) { this._kundali = value; this.render(); }
  get kundali() { return this._kundali ?? null; }

  render() {
    const k = this._kundali;
    this.textContent = '';
    if (!k) return;

    const locale = this.getAttribute('locale') || 'en';
    const useSymbols = this.getAttribute('labels') === 'symbols';

    const svg = el('svg', {
      viewBox: '-2 -2 104 104',
      class: 'chart chart--south',
      role: 'img',
      'aria-label': k.lagna
        ? `South Indian chart, lagna in ${localName(k.lagna.rashi.name, locale)}`
        : 'South Indian chart, birth time unknown so no lagna is shown'
    });

    // ⚠ The <svg> is in the document before the cells are filled —
    // renderCellLabels measures real text, which needs a rendered node.
    this.append(svg);

    // Occupancy by RĀŚI, not by bhāva — which is why this style needs no houses.
    const byRashi = Array.from({ length: 12 }, () => []);
    for (const g of Object.values(k.grahas)) byRashi[g.rashiIndex].push(g.graha);

    const lagnaRashi = k.lagna ? k.lagna.rashiIndex : null;

    for (let i = 0; i < 12; i++) {
      const cell = SOUTH_CELLS[i];
      const g = el('g', { class: 'chart__cell', 'data-rashi': i });
      g.append(el('rect', {
        x: cell.x, y: cell.y, width: cell.w, height: cell.h, class: 'chart__cell-area'
      }));
      svg.append(g);

      g.append(el('text', {
        x: cell.x + cell.w - 1.6, y: cell.y + 4.4,
        class: 'chart__rashi-label', 'text-anchor': 'end'
      }, rashi(i).abbr));

      // The bhāva number, shown only when the chart has houses at all.
      if (k.bhavas) {
        const bhavaNumber = ((i - lagnaRashi + 12) % 12) + 1;
        g.append(el('text', {
          x: cell.x + 1.6, y: cell.y + 4.4,
          class: 'chart__bhava-number', 'text-anchor': 'start'
        }, String(bhavaNumber)));
      }

      const occupants = byRashi[i];
      if (occupants.length) {
        // A rectangle, so the polygon for the width test is its four corners —
        // and the label block is kept below the rāśi and bhāva labels.
        const top = cell.y + 5.6;
        const poly = [
          [cell.x, top], [cell.x + cell.w, top],
          [cell.x + cell.w, cell.y + cell.h], [cell.x, cell.y + cell.h]
        ];
        renderCellLabels(g, occupants.map((id) => localName(graha(id).name, locale)), {
          cell: poly,
          anchor: [cell.x + cell.w / 2, (top + cell.y + cell.h) / 2],
          makeSpan: grahaSpan(k, occupants, locale),
          abbreviate: (_, idx) => graha(occupants[idx] ?? occupants[0]).name.symbol,
          baseFont: useSymbols ? 4.2 : 4.6
        });
      }

      if (i === lagnaRashi) {
        g.classList.add('is-lagna');
        // ◆ A diagonal across the corner is the traditional lagna mark in this
        // style, rather than a word, so it survives any locale.
        g.append(el('line', {
          x1: cell.x, y1: cell.y, x2: cell.x + cell.w * 0.42, y2: cell.y,
          class: 'chart__lagna-mark'
        }));
        g.append(el('line', {
          x1: cell.x, y1: cell.y, x2: cell.x, y2: cell.y + cell.h * 0.42,
          class: 'chart__lagna-mark'
        }));
      }
    }

    // The merged centre panel.
    const centre = el('g', { class: 'chart__centre' });
    centre.append(el('rect', {
      x: SOUTH_CENTRE.x, y: SOUTH_CENTRE.y,
      width: SOUTH_CENTRE.w, height: SOUTH_CENTRE.h, class: 'chart__centre-area'
    }));
    const title = this.getAttribute('title-text');
    if (title) {
      centre.append(el('text', {
        x: 50, y: 48, class: 'chart__title', 'text-anchor': 'middle'
      }, title));
    }
    if (!k.bhavas) {
      centre.append(el('text', {
        x: 50, y: title ? 56 : 51, class: 'chart__centre-note', 'text-anchor': 'middle'
      }, 'no birth time'));
      centre.append(el('text', {
        x: 50, y: title ? 62 : 57, class: 'chart__centre-note', 'text-anchor': 'middle'
      }, 'no bhāvas'));
    }
    svg.append(centre);
  }
}

/** One graha's <tspan>, with its state classes and a tooltip when abbreviated. */
function grahaSpan(kundali, occupants, locale) {
  return (label, index, abbreviated) => {
    const id = occupants[index] ?? occupants[0];
    const info = kundali.grahas[id];
    const span = el('tspan', {
      class: [
        'chart__graha',
        info?.retrograde ? 'is-retrograde' : null,
        info?.combust ? 'is-combust' : null
      ].filter(Boolean).join(' '),
      'data-graha': id
    }, label + (info?.retrograde ? '\u2009\u211E' : ''));
    if (abbreviated) span.append(el('title', {}, localName(graha(id).name, locale)));
    return span;
  };
}


customElements.define('chart-south', ChartSouth);
